import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

// CSS
import {
    WarningCancelOutsourceOrder,
    WarningDeclineOutsourceOrder,
    WarningDeclineReasonOutsourceOrder,
} from "../../../configs/outsourceOrder";
import { OutsourceOrderWrapper } from "../Styled";

// API + data
import apiMethods from "../../../utils/api";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import { OUTSOURCE_ORDER_STATUS } from "../../../data/enums";
import { OutsourceOrder } from "../../../data/entities";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";

// Shared Components
import PageLoader from "../../../shared/components/LoadingPage";
import DeclineModal from "../../../shared/components/GenericTextboxModal";
import DeclineReasonModal from "../../../shared/components/ReasonTextModal";

import StatusProgress from "./StatusProgress";
import { SingleOutsourceOrder } from "../single";
import OutsourceOrderTabNavigation from "./TabBar";
import { ExportMaterialModal } from "../../MaterialList/ExportMaterialModal";
import { ImportRequestList } from "../single/ImportRequestList";
import { BalanceSummary } from "../single/BalanceSummary";
import OrderTemplate from "../../../templates/don_gia_cong.docx";
import { buildOutsourceOrderDocument } from "./orderDocument";
import Template from "../../../templates/export_material_template.docx";
import { saveAs } from "file-saver";
import { generateDocumentBlob, generateDocument } from "../../../utils/exportUtils";
import { OutsourceOrderHeader } from "./OutsourceOrderHeader";
import { getOutsourceOrderActions } from "./actions";
import { useActiveMaterialsCatalog } from "../hooks";
import {
    hasOutsourceSourceMaterialShortage,
    normalizeOutsourceMaterials,
    resolveOutsourceMaterialsWithCatalog,
    serializeOutsourceMaterials,
    serializeStorageKeeperIds,
} from "../utils";

const processOutsourceOrder = (outsourceOrder: OutsourceOrder) => {
    if (outsourceOrder) {
        const baseOrder: any = outsourceOrder;
        const materials = normalizeOutsourceMaterials(outsourceOrder.materials || []);
        return Object.assign({}, baseOrder, {
            created_at: dayjs(outsourceOrder.created_at).format("YYYY-MM-DD"),
            materials,
        });
    }
    return { materials: [] } as any;
};

const prepareOutsourceOrder = (outsourceOrder: any) => {
    const materials = Array.isArray(outsourceOrder.materials) ? outsourceOrder.materials : [];
    const processedMaterials = serializeOutsourceMaterials(materials);

    const outsourceOrderPrice = Math.round(
        processedMaterials.reduce(
            (total: number, item: any) => total + (item.provider_price || 0) * (item.quantity || 0),
            0,
        )
    );

    return {
        ...outsourceOrder,
        materials: processedMaterials,
        logistic_staff: outsourceOrder.logistic_staff?.id,
        first_storage_keeper: serializeStorageKeeperIds(outsourceOrder.first_storage_keeper),
        second_storage_keeper: serializeStorageKeeperIds(outsourceOrder.second_storage_keeper),
        manager: outsourceOrder.manager?.id,
        total_price: outsourceOrderPrice,
    };
};

export const OutsourceOrderDetail = () => {
    const { outsourceOrderId } = useParams();
    const [{ data, isLoading: isOrderDataLoading, setLocalData }, fetchOutsourceOrder] = api.get(
        `/materials/outsource-orders/${outsourceOrderId}/`
    );
    const processedOutsourceOrder: OutsourceOrder = useMemo(() => processOutsourceOrder(data), [data]);
    const processedMaterials = Array.isArray(processedOutsourceOrder.materials)
        ? processedOutsourceOrder.materials
        : [];
    const {
        materials: catalogMaterials,
    } = useActiveMaterialsCatalog(processedMaterials.length === 0);
    const outsourceOrder: OutsourceOrder = useMemo(() => ({
        ...processedOutsourceOrder,
        materials: resolveOutsourceMaterialsWithCatalog(processedMaterials, catalogMaterials),
    }), [processedOutsourceOrder, processedMaterials, catalogMaterials]);

    const [isOpenDeclineModal, setIsOpenDeclineModal] = useState(false);
    const [showDeclineReasonModal, setShowDeclineReasonModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [hasExportedInSession, setHasExportedInSession] = useState(false);
    const [isDirectMutation, setIsDirectMutation] = useState(false);
    const isLoading: boolean = isOrderDataLoading;
    const [{ isUpdating }, setNewData] = api.patch(`/materials/outsource-orders/${outsourceOrderId}/`);
    const hasExportData = Boolean(outsourceOrder?.materials_export) ||
        Boolean(outsourceOrder?.export_transactions?.some(item => item.transaction_type === "OUT"));
    const isExported = hasExportData || hasExportedInSession;

    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const hasSourceMaterialShortage = useMemo(
        () => (outsourceOrder as any).source_first ? outsourceOrder.materials.some((m: any) => Number(m.selectedQuantity) > Number(m.available_quantity ?? m.quantity ?? 0)) : hasOutsourceSourceMaterialShortage(outsourceOrder.materials),
        [outsourceOrder.materials],
    );
    const actionState = useMemo(
        () => getOutsourceOrderActions({
            role,
            order: outsourceOrder,
            isExported,
            hasSourceMaterialShortage,
        }),
        [role, outsourceOrder, isExported, hasSourceMaterialShortage],
    );
    const isView = !actionState.canEdit;

    useEffect(() => {
        if (hasExportData || outsourceOrder?.status !== OUTSOURCE_ORDER_STATUS.EXPORTING) {
            setHasExportedInSession(false);
        }
    }, [hasExportData, outsourceOrder?.status]);

    const onClickDecline = async (reason?: string) => {
        if (isUpdating) return;
        try {
            await setNewData({ status: OUTSOURCE_ORDER_STATUS.CANCELLED, note: reason || "" });
            await fetchOutsourceOrder();
            setIsOpenDeclineModal(false);
            success("THÀNH CÔNG", "Đã từ chối đơn hàng thành công");
        } catch (err) {
            error("Từ chối đơn hàng thất bại", err);
        }
    };

    const onClickSendingToStorageKeeper = async () => {
        if (isUpdating) return;
        try {
            await setNewData({
                ...prepareOutsourceOrder(outsourceOrder),
                status: OUTSOURCE_ORDER_STATUS.EXPORTING,
            });
            await fetchOutsourceOrder();
            success("THÀNH CÔNG", "Gửi cho thủ kho thành công");
        } catch (err) {
            error("Gửi cho thủ kho thất bại", err);
        }
    };

    const onClickUpdateOutsourceOrder = async () => {
        if (isDirectMutation) return;
        setIsDirectMutation(true);
        try {
            const preparedOutsourceOrder = prepareOutsourceOrder(outsourceOrder);
            await apiMethods.patch(`/materials/outsource-orders/${outsourceOrderId}/`, preparedOutsourceOrder);
            await fetchOutsourceOrder();
            success("THÀNH CÔNG", "Cập nhật đơn hàng thành công");
        } catch (err) {
            error("Cập nhật đơn hàng thất bại", err);
        } finally {
            setIsDirectMutation(false);
        }
    };

    const onClickCompleteOrder = async () => {
        if (isDirectMutation) return;
        setIsDirectMutation(true);
        try {
            await apiMethods.patch(`/materials/outsource-orders/${outsourceOrderId}/`, {status: OUTSOURCE_ORDER_STATUS.COMPLETED});
            await fetchOutsourceOrder();
            success("THÀNH CÔNG", "Cập nhật đơn hàng thành công");
        } catch (err) {
            error("Cập nhật đơn hàng thất bại", err);
        } finally {
            setIsDirectMutation(false);
        }
    };

    const handleExport = async () => {
        await fetchOutsourceOrder();
        setHasExportedInSession(true);
        setShowExportModal(false);
    };

    const onToggleDeclineReasonModal = () => {
        setShowDeclineReasonModal((prev) => !prev);
    };

    const processMaterialsForExport = (materials: any[] = []) => {
        if ((outsourceOrder as any).source_first) return materials.map(m => ({ ...m, exportQuantity: m.selectedQuantity, available_quantity: m.available_quantity ?? m.quantity }));
        const sourceMaterials = new Map<string, any>();

        materials.forEach((outsourceMaterial: any) => {
            const outputQuantity = Number(outsourceMaterial.selectedQuantity) || 0;
            const standards = Array.isArray(outsourceMaterial.outsource_standards)
                ? outsourceMaterial.outsource_standards
                : [];

            standards.forEach((standard: any) => {
                const id = standard.source_material?.id || standard.source_material_id || standard.id;
                if (!id) return;

                const exportQuantity = Number(
                    (outputQuantity * (Number(standard.source_quantity_per_output) || 0)).toFixed(6),
                );
                const existingMaterial = sourceMaterials.get(String(id));
                if (existingMaterial) {
                    existingMaterial.exportQuantity += exportQuantity;
                    return;
                }

                const sourceMaterial = standard.source_material || standard;
                const availableQuantity = Number(
                    sourceMaterial.available_quantity ?? sourceMaterial.quantity ?? standard.quantity ?? 0,
                );
                sourceMaterials.set(String(id), {
                    ...sourceMaterial,
                    id,
                    name: sourceMaterial.name || standard.name || "",
                    material_code: sourceMaterial.material_code || standard.material_code || String(id),
                    measurement_unit: sourceMaterial.measurement_unit || standard.measurement_unit || "",
                    quantity: availableQuantity,
                    available_quantity: availableQuantity,
                    exportQuantity,
                });
            });
        });

        return Array.from(sourceMaterials.values()).filter((material) => material.exportQuantity > 0);
    };

    const handleDownloadOrder = async () => {
        try {
            const savedOrder = await apiMethods.get(`/materials/outsource-orders/${outsourceOrderId}/`) as OutsourceOrder;
            const blob = await generateDocumentBlob(buildOutsourceOrderDocument(savedOrder), OrderTemplate);
            saveAs(blob, `don_gia_cong_${savedOrder.name || savedOrder.id}.docx`);
        } catch (err) {
            error("Tải đơn hàng gia công thất bại", err);
        }
    };

    const handleDownloadExport = () => {
        if (!outsourceOrder.materials_export) {
            error("LỖI", "Không tìm thấy thông tin phiếu xuất kho");
            return;
        }

        const exportItem = outsourceOrder.materials_export;
        const itemDate = new Date(exportItem.created_at);
        const exportData = {
            storage: exportItem.storage,
            receiverer_name: exportItem.receiverer_name,
            export_method: exportItem.export_method,
            department: exportItem.department,
            source: exportItem.source,
            note: exportItem.note,
            day: itemDate.getDate(),
            month: itemDate.getMonth() + 1,
            year: itemDate.getFullYear(),
            materials: exportItem.materials.map((material, index) => ({
                index: index + 1,
                name: material.material.name,
                quantity: material.quantity,
                measurement_unit: material.material.measurement_unit,
                code: material.material.material_code
            }))
        };

        generateDocument(exportData, Template, `phieu_xuat_kho_${exportItem.storage}_${itemDate.toLocaleDateString('en-GB')}`);
    };

    const outsourceOrderInfoTab = isLoading ? (
        <PageLoader />
    ) : (
        <OutsourceOrderWrapper>
            <SingleOutsourceOrder
                outsourceOrder={outsourceOrder}
                mergeOutsourceOrder={(updatedData: any) =>
                    setLocalData({
                        ...data,
                        ...updatedData,
                    })
                }
                fetchOutsourceOrder={fetchOutsourceOrder}
                isView={isView}
            />
        </OutsourceOrderWrapper>
    );
    const outsourceOrderImportRequestsTab = isLoading ? (
        <PageLoader />
    ) : (
        <OutsourceOrderWrapper>
            <ImportRequestList
                outsourceOrder={outsourceOrder}
                balanceSummary={(outsourceOrder as any).balance_summary}
                fetchOutsourceOrder={fetchOutsourceOrder}
                isView={isView}
            />
        </OutsourceOrderWrapper>
    );
    const outsourceOrderTrackingTab = isLoading ? (
        <PageLoader />
    ) : (
        <OutsourceOrderWrapper>
            <BalanceSummary balanceSummary={(outsourceOrder as any).balance_summary} />
        </OutsourceOrderWrapper>
    );

    if (isLoading) return <PageLoader />;


    return (
        <>
            {isOpenDeclineModal && (
                <DeclineModal
                    open={isOpenDeclineModal}
                    title={
                        outsourceOrder.status === OUTSOURCE_ORDER_STATUS.OPEN
                            ? WarningCancelOutsourceOrder.title
                            : WarningDeclineOutsourceOrder.title
                    }
                    contentText={
                        outsourceOrder.status === OUTSOURCE_ORDER_STATUS.OPEN
                            ? WarningCancelOutsourceOrder.context
                            : WarningDeclineOutsourceOrder.context
                    }
                    reasonText={
                        outsourceOrder.status === OUTSOURCE_ORDER_STATUS.OPEN
                            ? WarningCancelOutsourceOrder.reason
                            : WarningDeclineOutsourceOrder.reason
                    }
                    handleConfirm={onClickDecline}
                    handleClose={() => setIsOpenDeclineModal(false)}
                    buttonText="Từ chối duyệt đơn hàng"
                    isLoading={isUpdating}
                />
            )}

            <DeclineReasonModal
                open={showDeclineReasonModal}
                title={WarningDeclineReasonOutsourceOrder.title}
                contentText={[outsourceOrder.note]}
                handleClose={onToggleDeclineReasonModal}
            />

            <ExportMaterialModal
                open={showExportModal}
                handleClose={() => setShowExportModal(false)}
                handleConfirm={handleExport}
                materials={processMaterialsForExport(outsourceOrder.materials)}
                contentType="OutsourceOrder"
                objectId={outsourceOrder.id}
            />

            <OutsourceOrderWrapper>
                <OutsourceOrderHeader
                    outsourceOrder={outsourceOrder}
                    actionState={actionState}
                    isLoading={isLoading}
                    isUpdating={isUpdating || isDirectMutation}
                    onSaveEdit={onClickUpdateOutsourceOrder}
                    onCancelOrder={() => setIsOpenDeclineModal(true)}
                    onSendToStorage={onClickSendingToStorageKeeper}
                    onOpenExport={() => setShowExportModal(true)}
                    onOpenReceipt={() => undefined}
                    onComplete={onClickCompleteOrder}
                    onDownloadExport={handleDownloadExport}
                    onDownloadOrder={handleDownloadOrder}
                />

                <StatusProgress status={outsourceOrder.status} />
                
                {isLoading ? (
                    <PageLoader />
                ) : (
                    <OutsourceOrderTabNavigation
                        outsourceOrderInfoTab={outsourceOrderInfoTab}
                        outsourceOrderImportRequestsTab={outsourceOrderImportRequestsTab}
                        outsourceOrderTrackingTab={outsourceOrderTrackingTab}
                    />
                )}
            </OutsourceOrderWrapper>
        </>
    );
};
